import { Button, Fields, Loader } from '@ets-global/b2c-website-ui';
import { Field as BaseFormikField, Form, Formik } from 'formik';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';

import HeaderMeta from '../../../components/HeaderMeta';
import { INFO_FORM_CONTACT_INDIVIDUAL } from '../../../graphql/mutations/contact';
import { getTranslatedUserCivility } from '../../../helpers/Formatter/UserFormatter';
import { useQuery, useRouter } from '../../../hooks';
import useYup from '../../../hooks/useYup';

const IndividualForm = ({ handleSubmit }) => {
  const { t } = useTranslation();
  const { Yup } = useYup();
  const { lang, countryEmoIso2 } = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { data: queryInfoFormContactIndividual, loading: loadingInfoFormContactIndividual } =
    useQuery(INFO_FORM_CONTACT_INDIVIDUAL);

  let civilities = queryInfoFormContactIndividual?.civilities || [];
  civilities = civilities.map(({ id }) => ({ label: getTranslatedUserCivility(id, t), value: id }));
  civilities.unshift({ value: '', label: '' });

  const formatData = async ({ rgpd, conditions, civility, ...data }) => {
    if (!executeRecaptcha) {
      return;
    }

    const recaptcha = await executeRecaptcha('b2c_website_contact_form');

    return {
      contactType: 'individual',
      preferredLanguage: lang.toUpperCase(),
      country: countryEmoIso2,
      civility: parseInt(civility),
      recaptcha,
      ...data,
    };
  };

  const IndividualContactFormSchema = Yup.object().shape({
    civility: Yup.string().required(),
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    email: Yup.string().email().required(),
    phone: Yup.string().phoneNumber().required(),
    subjectQuestion: Yup.string().required(),
    subjectOption: Yup.string().required(),
    message: Yup.string().required(),
    conditions: Yup.boolean().oneOf([true], t('common.form.validation.required')),
    rgpd: Yup.boolean().oneOf([true], t('common.form.validation.required')),
  });

  const subjectsQuestion = (queryInfoFormContactIndividual.salesforce?.subjectQuestions || []).map(
    ({ value, subjectOptions }) => {
      const subjectOptionValues = subjectOptions.map(({ value }) => ({
        label: t(`contact.form.options.subject-option.${value}`),
        value,
      }));
      subjectOptionValues.unshift({ label: '', value: '' });

      return { label: t(`contact.form.options.subject-question.${value}`), value, subjectOptions: subjectOptionValues };
    }
  );
  subjectsQuestion.unshift({ label: '', value: '' });

  return loadingInfoFormContactIndividual ? (
    <Loader />
  ) : (
    <>
      <HeaderMeta page={'contact-individual'} />
      <Formik
        initialValues={{
          civility: '',
          lastName: '',
          firstName: '',
          email: '',
          phone: '',
          subjectQuestion: '',
          subjectOption: '',
          message: '',
          conditions: false,
          rgpd: false,
        }}
        validationSchema={IndividualContactFormSchema}
        onSubmit={async (data) => {
          handleSubmit(await formatData(data));
        }}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form className={'contact-form'}>
            <div className="contact-form__main-inner">
              <div className="contact-form__main-column">
                <Fields.FormikFastField
                  id="civility"
                  name="civility"
                  component={Fields.SelectField}
                  label={t('contact.form.label.civility')}
                  autoComplete={'contact honorific-prefix'}
                  required={true}
                  options={civilities}
                  className={'field-wrapper--limited'}
                />
                <Fields.FormikFastField
                  id="lastName"
                  name="lastName"
                  component={Fields.TextField}
                  label={t('contact.form.label.lastname')}
                  autoComplete={'contact family-name'}
                  required={true}
                />
                <Fields.FormikFastField
                  id="firstName"
                  name="firstName"
                  component={Fields.TextField}
                  label={t('contact.form.label.firstname')}
                  autoComplete={'contact given-name'}
                  required={true}
                />
                <Fields.FormikFastField
                  id="email"
                  name="email"
                  component={Fields.TextField}
                  label={t('contact.form.label.email')}
                  type={'email'}
                  autoComplete={'contact email'}
                  required={true}
                />
                <BaseFormikField
                  component={Fields.PhoneNumberField}
                  id="phone"
                  name="phone"
                  className={'field--dotted'}
                  labels={[t('contact.form.label.country'), t('contact.form.label.phone1')]}
                  required={true}
                />
              </div>
              <div className="contact-form__main-column">
                <Fields.FormikFastField
                  id="subjectQuestion"
                  name="subjectQuestion"
                  component={Fields.SelectField}
                  label={t('contact.form.label.subject')}
                  autoComplete={'contact subjectQuestion'}
                  required={true}
                  options={subjectsQuestion}
                  onChange={() => {
                    setFieldValue('subjectOption', '');
                  }}
                />
                {values.subjectQuestion !== '' && (
                  <Fields.FormikFastField
                    id="subjectOption"
                    name="subjectOption"
                    component={Fields.SelectField}
                    label={t('contact.form.label.subject-option')}
                    autoComplete={'contact subjectOption'}
                    options={
                      subjectsQuestion.find((subjectQuestion) => subjectQuestion.value === values.subjectQuestion)
                        ?.subjectOptions
                    }
                    required={true}
                  />
                )}
                {values.subjectOption !== '' && (
                  <Fields.FormikFastField
                    id="message"
                    name="message"
                    component={Fields.TextareaField}
                    label={t('contact.form.label.individual.message.title')}
                    autoComplete={'contact message'}
                    onChange={(e) => {
                      e.target.style.height = '30px';
                      e.target.style.height = `${e.target.scrollHeight + 5}px`;
                    }}
                    instructions={t('contact.form.label.individual.message.instructions')}
                    maxLength={240}
                    required={true}
                  />
                )}
              </div>
            </div>
            <div className="contact-form__checkboxes">
              <Fields.FormikFastField
                id="rgpd"
                name="rgpd"
                component={Fields.CheckboxField}
                label={t('contact.form.label.individual.rgpd')}
                required={true}
              />

              <Fields.FormikFastField
                id="conditions"
                name="conditions"
                component={Fields.CheckboxField}
                label={t('contact.form.label.individual.conditions')}
                required={true}
              />
            </div>
            <div className="contact-form__buttons">
              <Button type="submit" disabled={isSubmitting} aria-label={t('contact.form.button.send')}>
                <Trans>contact.form.button.send</Trans>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default IndividualForm;
